import axios, { AxiosInstance } from "axios";
import { RpiApiContextProps, useRpiApiContext } from "./provider";

const restClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

const publicRestClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

export function getPublicRestClient() {
  if (!publicRestClient.defaults.baseURL) {
    throw new Error("Public rest client endpoint not set! Call configureRestClients before accessing.");
  }
  return publicRestClient;
}

export function getRestClient() {
  if (!restClient.defaults.baseURL) {
    throw new Error("Rest client endpoint not set! Call configureRestClients before accessing.");
  }
  return restClient;
}

export function configureRestClients(publicPrintApiEndpointUrl: string, clientApiUrl: string) {
  restClient.defaults.baseURL = publicPrintApiEndpointUrl;
  publicRestClient.defaults.baseURL = clientApiUrl;
}

// GraphQL
export async function rpiGraphqlRequest<TData, TVariables>(
  graphqlClient: AxiosInstance,
  query: string,
  variables?: TVariables,
  headers?: Record<string, string>
): Promise<TData> {
  return graphqlClient
    .post<GraphQLResponseBody<TData>>("", { query, variables }, { headers })
    .then(({ data: body }) => {
      const [firstError] = body.errors ?? [];
      if (firstError) {
        throw new CustomGraphQLError(firstError);
      }
      return body.data;
    });
}

export function useRawGraphqlRequest() {
  const rpiApiContext = useRpiApiContext();

  const useGraphqlRequest = async <TData, TVariables>(query: string, variables?: TVariables): Promise<TData> => {
    return rpiGraphqlRequest(rpiApiContext.graphqlClient, query, variables, {
      ...(await getHeaders(rpiApiContext.headers)),
    });
  };

  return useGraphqlRequest;
}

export const useRpiGraphqlRequest = <TData, TVariables>(
  query: string,
  options?: Record<string, string>
): ((variables?: TVariables) => Promise<TData>) => {
  const rpiApiContext = useRpiApiContext();

  return async (variables) => {
    return rpiGraphqlRequest(rpiApiContext.graphqlClient, query, variables, {
      ...(await getHeaders(rpiApiContext.headers)),
      ...options,
    });
  };
};

export const getHeaders = async (headers: RpiApiContextProps["headers"]) => {
  if (typeof headers === "function") {
    return await Promise.resolve(headers());
  } else {
    return headers;
  }
};

/** Fields our GraphQL backend attaches to an error's `extensions`; absent on transport or validation errors. */
export interface CustomErrorExtensions {
  code: number;
  classification: string;
}

/** A GraphQL error entry as returned by the backend. `extensions` is free-form per the spec. */
export interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: Array<string | number>;
  extensions?: Record<string, unknown>;
}

/** A {@link GraphQLError} whose `extensions` carry the backend's {@link CustomErrorExtensions}. */
export interface CustomGraphQLErrorData extends GraphQLError {
  extensions: CustomErrorExtensions & Record<string, unknown>;
}

interface GraphQLResponseBody<TData> {
  data: TData;
  errors?: GraphQLError[];
}

export class CustomGraphQLError extends Error {
  public readonly data: GraphQLError;

  constructor(data: GraphQLError) {
    super(data.message);
    this.name = "GraphQLError";
    this.data = data;
  }
}

/** Narrows to any GraphQL error surfaced by the client; guarantees a usable `message`. */
export const isGraphQLError = (error: unknown): error is CustomGraphQLError => error instanceof CustomGraphQLError;

/** Narrows to a GraphQL error carrying the backend's {@link CustomErrorExtensions} (`code` + `classification`). */
export const isCustomGraphQLError = (error: unknown): error is CustomGraphQLError & { data: CustomGraphQLErrorData } =>
  isGraphQLError(error) &&
  typeof error.data.extensions?.code === "number" &&
  typeof error.data.extensions?.classification === "string";
